import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Truck, Plus, ArrowLeft, Phone, Mail,
  BarChart3, Package, Users, Upload, UserCheck, UserX, Trash2, Pencil, Key
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

export default function DriversPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    full_name: "",
    role: "driver",
    email: "",
    phone: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleting, setDeleting] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [editData, setEditData] = useState({ full_name: "", email: "", phone: "", username: "" });
  const [editing, setEditing] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.full_name) {
      toast.error("Username, password, and name are required");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/users", formData);
      toast.success("User created successfully");
      setDialogOpen(false);
      setFormData({
        username: "",
        password: "",
        full_name: "",
        role: "driver",
        email: "",
        phone: ""
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserActive = async (userId) => {
    try {
      await api.put(`/users/${userId}/toggle-active`);
      toast.success("User status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteDialog.user.id}`);
      toast.success(`User "${deleteDialog.user.full_name}" deleted successfully`);
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (userToEdit) => {
    setEditData({
      full_name: userToEdit.full_name || "",
      email: userToEdit.email || "",
      phone: userToEdit.phone || "",
      username: userToEdit.username || ""
    });
    setEditDialog({ open: true, user: userToEdit });
  };

  const handleEditUser = async () => {
    if (!editDialog.user) return;
    
    setEditing(true);
    try {
      await api.put(`/users/${editDialog.user.id}`, editData);
      toast.success("User updated successfully");
      setEditDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setEditing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordDialog.user || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.put(`/users/${passwordDialog.user.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${passwordDialog.user.full_name}`);
      setPasswordDialog({ open: false, user: null });
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const drivers = users.filter(u => u.role === "driver");
  const admins = users.filter(u => u.role === "admin");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="drivers-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#003366] text-white hidden lg:block">
        <div className="p-6">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-12 mb-6" />
          <p className="text-sm text-blue-200">Admin Dashboard</p>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <BarChart3 className="h-5 w-5" />
            Dashboard
          </Link>
          <Link to="/admin/jobs" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Package className="h-5 w-5" />
            Delivery Jobs
          </Link>
          <Link to="/admin/customers" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Users className="h-5 w-5" />
            Customers
          </Link>
          <Link to="/admin/drivers" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <Truck className="h-5 w-5" />
            Drivers
          </Link>
          <Link to="/admin/upload" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Upload className="h-5 w-5" />
            Import Data
          </Link>
        </nav>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="font-semibold">Drivers & Users</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Drivers & Users</h1>
              <p className="text-slate-600">{drivers.length} drivers, {admins.length} admins</p>
            </div>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="add-user-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>

          {/* Drivers Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Truck className="h-5 w-5 text-[#003366]" />
              Drivers ({drivers.length})
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {drivers.map((driver) => (
                <Card key={driver.id} className="card-nassau" data-testid={`driver-${driver.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{driver.full_name}</h3>
                          <Badge variant={driver.is_active ? "default" : "secondary"} className={driver.is_active ? "bg-emerald-100 text-emerald-800" : ""}>
                            {driver.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">@{driver.username}</p>
                        <div className="space-y-1 mt-3 text-sm text-slate-600">
                          {driver.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-slate-400" />
                              <span>{driver.phone}</span>
                            </div>
                          )}
                          {driver.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-slate-400" />
                              <span>{driver.email}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{driver.is_active ? "Active" : "Disabled"}</span>
                          <Switch
                            checked={driver.is_active}
                            onCheckedChange={() => toggleUserActive(driver.id)}
                            data-testid={`toggle-${driver.id}`}
                          />
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openEditDialog(driver)}
                            data-testid={`edit-driver-${driver.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => setPasswordDialog({ open: true, user: driver })}
                            data-testid={`reset-password-${driver.id}`}
                          >
                            <Key className="h-4 w-4 mr-1" />
                            Reset
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialog({ open: true, user: driver })}
                          data-testid={`delete-driver-${driver.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {drivers.length === 0 && (
              <div className="text-center py-8 bg-slate-100 rounded-lg">
                <Truck className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">No drivers yet</p>
              </div>
            )}
          </div>

          {/* Admins Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-[#003366]" />
              Administrators ({admins.length})
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {admins.map((admin) => (
                <Card key={admin.id} className="card-nassau" data-testid={`admin-${admin.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{admin.full_name}</h3>
                        <p className="text-sm text-slate-500">@{admin.username}</p>
                        {admin.email && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span>{admin.email}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className="bg-[#003366]">Admin</Badge>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openEditDialog(admin)}
                            data-testid={`edit-admin-${admin.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => setPasswordDialog({ open: true, user: admin })}
                            data-testid={`reset-password-admin-${admin.id}`}
                          >
                            <Key className="h-4 w-4 mr-1" />
                            Reset
                          </Button>
                        </div>
                        {admin.id !== user?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteDialog({ open: true, user: admin })}
                            data-testid={`delete-admin-${admin.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Smith"
                data-testid="user-fullname-input"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="jsmith"
                  data-testid="user-username-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="user-password-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Driver</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                  data-testid="user-phone-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="save-user-btn"
              >
                {submitting ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: open ? deleteDialog.user : null })}>
        <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">
              Are you sure you want to delete <strong>{deleteDialog.user?.full_name}</strong> (@{deleteDialog.user?.username})?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              This action cannot be undone. The user will be permanently removed from the system.
            </p>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, user: null })}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, user: open ? editDialog.user : null })}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-blue-600 flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-fullname">Full Name</Label>
              <Input
                id="edit-fullname"
                value={editData.full_name}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-username">Username</Label>
              <Input
                id="edit-username"
                value={editData.username}
                onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                placeholder="jsmith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                placeholder="+353 1234567"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setEditDialog({ open: false, user: null })}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEditUser}
              disabled={editing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => { setPasswordDialog({ open, user: open ? passwordDialog.user : null }); if (!open) setNewPassword(""); }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <Key className="h-5 w-5" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-700">
              Set a new password for <strong>{passwordDialog.user?.full_name}</strong>
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => { setPasswordDialog({ open: false, user: null }); setNewPassword(""); }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleResetPassword}
              disabled={resettingPassword || newPassword.length < 6}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {resettingPassword ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Navigation */}
      <nav className="lg:hidden mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="h-full flex items-center justify-around">
          <Link to="/admin" className="flex flex-col items-center gap-1 text-slate-400">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex flex-col items-center gap-1 text-slate-400">
            <Package className="h-5 w-5" />
            <span className="text-xs">Jobs</span>
          </Link>
          <Link to="/admin/customers" className="flex flex-col items-center gap-1 text-slate-400">
            <Users className="h-5 w-5" />
            <span className="text-xs">Customers</span>
          </Link>
          <Link to="/admin/drivers" className="flex flex-col items-center gap-1 text-[#003366]">
            <Truck className="h-5 w-5" />
            <span className="text-xs">Drivers</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
